import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function AuthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "signup" ? "signup" : "signin";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { user, login, register, signInWithGoogle, sendPasswordReset } = useAuth();

  useEffect(() => {
    if (user && typeof user === "object") navigate("/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");
    try {
      if (mode === "signup") {
        const result = await register(email, password, name);
        if (!result) {
          // email confirmation needed
          setInfo("Account created! Check your email to confirm, then sign in.");
          setMode("signin");
          return;
        }
      } else {
        await login(email, password);
      }
      navigate("/dashboard");
    } catch (err) {
      const msg = err?.message || "Something went wrong.";
      // If email is already registered on signup, nudge them into sign-in
      if (
        mode === "signup" &&
        /already\s*(registered|exists)/i.test(msg)
      ) {
        setError("That email already has an account — please sign in instead.");
        setMode("signin");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="auth-page">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 overflow-hidden bg-gradient-hero grain lg:flex lg:flex-col lg:items-start lg:justify-between lg:p-12">
        <div className="relative z-10">
          <Logo size="lg" variant="white" />
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="font-heading text-4xl font-extrabold leading-tight text-primary-foreground">
            Peace of mind for parents. Prepared for anything.
          </h2>
          <p className="mt-4 text-lg text-primary-foreground/80">
            Track every dose, every appointment, every milestone — for everyone you love.
          </p>
          <div className="mt-8 flex items-center gap-3 text-primary-foreground/70">
            <ShieldCheck className="h-5 w-5 text-accent" />
            <span className="text-sm">Private, organized, always with you.</span>
          </div>
        </div>
        <img
          src="/brand/hero-illustration.png"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -bottom-10 -right-10 z-0 w-[480px] opacity-25"
        />
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <button
            onClick={() => navigate("/")}
            className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="auth-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </button>

          <div className="mb-8 lg:hidden">
            <Logo size="md" />
          </div>

          <h1 className="font-heading text-3xl font-extrabold tracking-tight">
            {mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {mode === "signin"
              ? "Sign in to your family's health hub."
              : "Get started organizing your family's health."}
          </p>

          <div className="mt-6">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              size="lg"
              onClick={async () => {
                setError("");
                setInfo("");
                setGoogleLoading(true);
                try {
                  await signInWithGoogle();
                } catch (err) {
                  const raw = err?.message || "Google sign-in failed";
                  if (/unsupported provider|missing oauth secret|provider is not enabled/i.test(raw)) {
                    setError(
                      "Google Sign-In isn't set up on this project yet. Please use email + password below, or ask your admin to finish configuring the Google provider in Supabase Dashboard → Authentication → Providers → Google (Client ID + Secret required)."
                    );
                  } else {
                    setError(raw);
                  }
                  setGoogleLoading(false);
                }
              }}
              disabled={googleLoading}
              data-testid="auth-google-btn"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z"/>
                <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83Z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/>
              </svg>
              {googleLoading ? "Redirecting…" : "Continue with Google"}
            </Button>
            <div className="my-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  data-testid="auth-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  required
                  className="mt-1.5"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
                className="mt-1.5"
              />
            </div>

            {error && (
              <div
                className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            {info && (
              <div
                className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"
                data-testid="auth-info"
              >
                {info}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={loading}
              data-testid="auth-submit"
            >
              {loading
                ? "Please wait…"
                : mode === "signin"
                ? "Sign In"
                : "Create Account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "signin" ? (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    setError("");
                    setInfo("");
                    if (!email) {
                      setError("Enter your email above first, then tap 'Forgot password?'.");
                      return;
                    }
                    setResetLoading(true);
                    try {
                      await sendPasswordReset(email);
                      setInfo(
                        `Password reset link sent to ${email}. Check your inbox (and spam folder).`
                      );
                    } catch (err) {
                      setError(err?.message || "Couldn't send reset link.");
                    } finally {
                      setResetLoading(false);
                    }
                  }}
                  className="text-sm font-semibold text-primary hover:underline disabled:opacity-50"
                  disabled={resetLoading}
                  data-testid="auth-forgot-password"
                >
                  {resetLoading ? "Sending link…" : "Forgot password?"}
                </button>
                <span className="mx-2 text-muted-foreground/40">•</span>
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("signup");
                    setError("");
                  }}
                  className="font-semibold text-primary hover:underline"
                  data-testid="auth-toggle-signup"
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setError("");
                  }}
                  className="font-semibold text-primary hover:underline"
                  data-testid="auth-toggle-signin"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
